import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleNotch } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import AuthShell from '../components/layout/AuthShell';
import { authApi } from '../lib/api';
import { formatApiErrorDetail } from '../lib/utils';
import { emailErrorMessage, validateEmailParts } from '../lib/contactValidation';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [doneMsg, setDoneMsg] = useState('');
  const [devExtras, setDevExtras] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setDoneMsg('');
    setDevExtras(null);
    const emailCheck = validateEmailParts(email);
    if (!emailCheck.valid) {
      setError(emailErrorMessage(emailCheck, t));
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.forgotPassword(emailCheck.email);
      setDoneMsg(res.data?.message || t('auth.forgotPasswordSent'));
      if (res.data?.email_sent === false && !res.data?.reset_token) {
        setError(
          res.data?.delivery_note ||
            'We could not send the email right now. Please try again in a few minutes or contact your administrator.',
        );
        setDoneMsg('');
      }
      if (res.data?.reset_token) {
        setDevExtras({
          token: res.data.reset_token,
          link: res.data.reset_link || '',
          note: res.data?.delivery_note || '',
        });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.forgotPasswordTitle')}
      description={t('auth.forgotPasswordDesc') || 'Enter your email and we will send a reset link.'}
      footer={(
        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
            {t('auth.backToSignIn')}
          </Link>
        </p>
      )}
    >
      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}
      {doneMsg && (
        <div className="mb-6 space-y-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
          <p>{doneMsg}</p>
          {devExtras?.link && (
            <div className="border-t border-emerald-100 pt-2 dark:border-emerald-500/20">
              {devExtras.note && <p className="mb-2 text-xs text-amber-800 dark:text-amber-300">{devExtras.note}</p>}
              <p className="mb-1 text-xs font-medium">{t('auth.forgotPasswordDevLink')}</p>
              <a href={devExtras.link} className="break-all text-xs text-brand-600">{devExtras.link}</a>
            </div>
          )}
          {devExtras?.token && !devExtras?.link && (
            <p className="break-all font-mono text-xs">{devExtras.token}</p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="fp-email">{t('common.email')}</Label>
          <Input
            id="fp-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.emailPlaceholder')}
            required
            autoComplete="email"
          />
        </div>

        <Button type="submit" disabled={loading} className="w-full">
          {loading ? <CircleNotch className="h-5 w-5 animate-spin" /> : t('auth.forgotPasswordSubmit')}
        </Button>
      </form>
    </AuthShell>
  );
}
